import { formatCalendarDate } from '@cerebro-diem/core';
import type { Task, TaskStatus } from '@cerebro-diem/core';
import { supabase } from './supabase';

export type TaskUpdate = Partial<
  Pick<Task, 'name' | 'due_date' | 'notes' | 'status' | 'focus_date' | 'completed_at' | 'project_id' | 'area_id' | 'gtd_status' | 'context'>
>;

export const TASK_STATUSES: readonly TaskStatus[] = ['pending', 'in_progress', 'done'];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  done: 'Done',
};

/** A status change together with the completion timestamp that moves with it. */
export function statusUpdate(status: TaskStatus): TaskUpdate {
  return { status, completed_at: status === 'done' ? new Date().toISOString() : null };
}

/**
 * Save a task edit in one database call (save_task_edit): its columns and,
 * when `reminder` is given, its reminder (a wall time in the user's zone, or
 * null to clear). A refused reminder saves nothing; the error says why.
 */
export async function saveTaskEdit(id: string, update: TaskUpdate, reminder?: string | null): Promise<void> {
  const { error } = await supabase.rpc('save_task_edit', {
    p_task_id: id,
    p_changes: update,
    p_set_reminder: reminder !== undefined,
    p_reminder: reminder ?? null,
  });
  if (error) throw error;
}

/** "Due today", "Due Sat, Sep 12", or "Overdue · was due Fri, Sep 11" for an open task. Dates are YYYY-MM-DD. */
export function dueLabel(dueDate: string, today: string): string {
  if (dueDate < today) return `Overdue · was due ${formatCalendarDate(dueDate)}`;
  if (dueDate === today) return 'Due today';
  return `Due ${formatCalendarDate(dueDate)}`;
}
